import { TFunction } from "i18next";
import {
  HeatOverrideMaterialDelta,
  MixBasket,
} from "src/store/api/generatedApi";
import { createHeatOverrideBodyOneChargeDeltasItemMaterialDeltasMax } from "src/store/api/generatedZodApi/deployments";
import { v4 as uuidv4 } from "uuid";

export const MAX_MATERIAL_DELTAS_PER_CHARGE =
  createHeatOverrideBodyOneChargeDeltasItemMaterialDeltasMax;

export type DeltaSign = "plus" | "minus";

export interface MaterialDeltaRow {
  rowKey: string;
  materialName: string | null;
  sign: DeltaSign;
  weightInput: string;
}

export type MaterialDeltaProblem =
  | { kind: "noMaterial" }
  | { kind: "noWeight" }
  | { kind: "negativeTotal"; materialName: string };

export type MaterialDeltaResult =
  | { kind: "valid"; materialDeltas: HeatOverrideMaterialDelta[] }
  | { kind: "invalid"; problem: MaterialDeltaProblem };

export const emptyMaterialDeltaRow = (): MaterialDeltaRow => ({
  rowKey: uuidv4(),
  materialName: null,
  sign: "plus",
  weightInput: "0",
});

export const plannedMassByMaterial = (basket: MixBasket): Map<string, number> =>
  basket.layers
    .filter((layer) => layer.quantity.unit === "mass")
    .reduce(
      (totals, layer) =>
        new Map(totals).set(
          layer.material_name,
          (totals.get(layer.material_name) ?? 0) + layer.quantity.value
        ),
      new Map<string, number>()
    );

/** Returns the row's signed mass change in tenant mass units, or null for an invalid weight. */
export const signedMassDelta = ({
  row,
  massScaleFactor,
}: {
  row: MaterialDeltaRow;
  massScaleFactor: number;
}): number | null => {
  const displayWeight = Number(row.weightInput);
  if (!Number.isFinite(displayWeight) || displayWeight <= 0) {
    return null;
  }
  return ((row.sign === "plus" ? 1 : -1) * displayWeight) / massScaleFactor;
};

/** Converts the rows to API deltas in tenant mass units, or names the first problem. */
export const resolveMaterialDeltas = ({
  rows,
  basket,
  massScaleFactor,
}: {
  rows: MaterialDeltaRow[];
  basket: MixBasket;
  massScaleFactor: number;
}): MaterialDeltaResult => {
  const namedRows = rows.flatMap((row) =>
    row.materialName === null
      ? []
      : [{ ...row, materialName: row.materialName }]
  );
  if (namedRows.length < rows.length) {
    return { kind: "invalid", problem: { kind: "noMaterial" } };
  }
  const materialDeltas = namedRows.flatMap((row) => {
    const massDelta = signedMassDelta({ row, massScaleFactor });
    return massDelta === null
      ? []
      : [{ material_name: row.materialName, mass_delta_per_heat: massDelta }];
  });
  if (materialDeltas.length < namedRows.length) {
    return { kind: "invalid", problem: { kind: "noWeight" } };
  }
  const plannedMass = plannedMassByMaterial(basket);
  const negative = materialDeltas.find(
    (delta) =>
      (plannedMass.get(delta.material_name) ?? 0) + delta.mass_delta_per_heat <
      0
  );
  if (negative !== undefined) {
    return {
      kind: "invalid",
      problem: { kind: "negativeTotal", materialName: negative.material_name },
    };
  }
  return { kind: "valid", materialDeltas };
};

export const materialDeltaHint = ({
  problem,
  t,
}: {
  problem: MaterialDeltaProblem;
  t: TFunction;
}): string => {
  switch (problem.kind) {
    case "noMaterial":
      return t("deltaSelectMaterialHint");
    case "noWeight":
      return t("deltaEnterWeightHint");
    case "negativeTotal":
      return t("deltaInvalidQuantityHint", { material: problem.materialName });
  }
};
